import "./theme.css";
import "./styles.css";
import { ROLE_LABELS } from "../shared/order-domain";
import { ApiError, request, write, type Session, type VisibleOrder } from "./api";
import { initializeOrderForm } from "./order-form";
import { isOrderFilter, renderOrders, type OrderFilter } from "./orders-view";
import { initializePartnerActions } from "./partner-actions";
import { copyText, element, formError, showToast } from "./ui";
import { initializeProfitDialog } from "./profit-dialog";
import { initializeTheme } from "./theme";

initializeTheme();

let session: Session | null = null;
let orders: VisibleOrder[] = [];
let filter: OrderFilter = "active";
let search = "";
let refreshTimer: number | undefined;
let sessionEpoch = 0;
let refreshing = false;
let deletingId: number | null = null;
let deleting = false;
const deleteDialog = element<HTMLDialogElement>("deleteDialog");

function render(): void {
  if (session) renderOrders(orders, session.role, filter, search);
  profitDialog.refresh();
}

function replaceOrder(order: VisibleOrder): void {
  if (!session) return;
  const index = orders.findIndex((candidate) => candidate.id === order.id);
  if (index === -1) orders.unshift(order);
  else orders[index] = order;
  render();
}

const profitDialog = initializeProfitDialog(() => orders, () => session?.role ?? null);
const orderForm = initializeOrderForm(replaceOrder);
const partnerActions = initializePartnerActions(replaceOrder, (id) => orders.find((order) => order.id === id));

function showGate(): void {
  sessionEpoch += 1;
  session = null;
  orders = [];
  partnerActions.reset();
  profitDialog.reset();
  element("orders").replaceChildren();
  element("metrics").replaceChildren();
  element("appRoot").hidden = true;
  element("loginGate").hidden = false;
  element("roleLabel").hidden = true;
  element("logoutButton").hidden = true;
  element<HTMLInputElement>("loginCode").value = "";
  document.querySelectorAll<HTMLDialogElement>("dialog[open]").forEach((dialog) => dialog.close());
  document.querySelectorAll<HTMLFormElement>("dialog form").forEach((form) => form.reset());
  window.clearInterval(refreshTimer);
  refreshing = false;
}

async function refresh(silent = false): Promise<void> {
  if (!session || refreshing) return;
  const epoch = sessionEpoch;
  refreshing = true;
  const button = element<HTMLButtonElement>("refreshButton");
  if (!silent) { button.disabled = true; button.textContent = "正在刷新…"; }
  try {
    const result = await request<VisibleOrder[]>("/orders");
    if (epoch !== sessionEpoch) return;
    orders = result;
    formError("loadError", null);
    render();
    element("updatedAt").textContent = `同步于 ${new Date().toLocaleTimeString("zh-CN", { hour12: false, hour: "2-digit", minute: "2-digit" })}`;
  } catch (error) {
    if (epoch === sessionEpoch) {
      formError("loadError", error);
      if (!silent) showToast(error instanceof Error ? error.message : "加载失败", true);
    }
  } finally {
    if (epoch === sessionEpoch) refreshing = false;
    if (!silent) { button.disabled = false; button.textContent = "刷新订单"; }
  }
}

async function enter(nextSession: Session): Promise<void> {
  sessionEpoch += 1;
  session = nextSession;
  orders = [];
  profitDialog.reset();
  filter = "active";
  search = "";
  element<HTMLInputElement>("searchInput").value = "";
  element("filters").querySelectorAll<HTMLButtonElement>("[data-filter]").forEach((button) => {
    button.classList.toggle("active", button.dataset.filter === filter);
    button.setAttribute("aria-pressed", String(button.dataset.filter === filter));
  });
  element("roleLabel").textContent = ROLE_LABELS[session.role];
  element("roleLabel").hidden = false;
  element("logoutButton").hidden = false;
  element("addButton").hidden = session.role !== "owner";
  element("workspaceDescription").textContent = session.role === "owner" ? "上传文件，记录地址，查看发货与结算进度。" : "下载文件，寄出订单，回填报价并确认收款。";
  element("loginGate").hidden = true;
  element("appRoot").hidden = false;
  render();
  await refresh();
  window.clearInterval(refreshTimer);
  if (!session) return;
  refreshTimer = window.setInterval(() => {
    if (!document.hidden && !document.querySelector("dialog[open]")) void refresh(true);
  }, 30_000);
}

element<HTMLFormElement>("loginForm").addEventListener("submit", async (event) => {
  event.preventDefault();
  const button = element<HTMLButtonElement>("loginSubmit");
  button.disabled = true;
  formError("loginError", null);
  try {
    const nextSession = await write<Session>("/auth/login", "POST", { code: element<HTMLInputElement>("loginCode").value });
    element<HTMLInputElement>("loginCode").value = "";
    await enter(nextSession);
  } catch (error) { formError("loginError", error); }
  finally { button.disabled = false; }
});
element("logoutButton").addEventListener("click", async () => {
  try { await write("/auth/logout", "POST", {}); showGate(); }
  catch (error) { showToast(error instanceof Error ? error.message : "退出失败", true); }
});
window.addEventListener("sessionexpired", showGate);
element("refreshButton").addEventListener("click", () => void refresh());
element("addButton").addEventListener("click", () => { if (session?.role === "owner") orderForm.open(); });
element<HTMLInputElement>("searchInput").addEventListener("input", (event) => { search = (event.target as HTMLInputElement).value; render(); });
element("filters").addEventListener("click", (event) => {
  const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>("[data-filter]") : null;
  if (!button || !isOrderFilter(button.dataset.filter)) return;
  filter = button.dataset.filter;
  element("filters").querySelectorAll<HTMLButtonElement>("button").forEach((candidate) => {
    candidate.classList.toggle("active", candidate === button);
    candidate.setAttribute("aria-pressed", String(candidate === button));
  });
  render();
});

deleteDialog.querySelectorAll<HTMLButtonElement>("[data-close]").forEach((button) => button.addEventListener("click", () => { if (!deleting) deleteDialog.close(); }));
deleteDialog.addEventListener("cancel", (event) => { if (deleting) event.preventDefault(); });
deleteDialog.addEventListener("close", () => { deletingId = null; });
element<HTMLFormElement>("deleteForm").addEventListener("submit", async (event) => {
  event.preventDefault();
  if (deleting || deletingId === null || session?.role !== "owner") return;
  const id = deletingId;
  const epoch = sessionEpoch;
  deleting = true;
  const buttons = deleteDialog.querySelectorAll<HTMLButtonElement>("button");
  buttons.forEach((button) => { button.disabled = true; });
  formError("deleteError", null);
  try {
    await request(`/orders/${id}`, { method: "DELETE" });
    if (epoch !== sessionEpoch) return;
    orders = orders.filter((order) => order.id !== id);
    render();
    deleteDialog.close();
    showToast("订单已删除");
  } catch (error) { if (epoch === sessionEpoch) formError("deleteError", error); }
  finally { deleting = false; buttons.forEach((button) => { button.disabled = false; }); }
});

element("orders").addEventListener("click", async (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest<HTMLButtonElement>("[data-action]");
  const host = event.target.closest<HTMLElement>("[data-id]");
  const order = orders.find((candidate) => String(candidate.id) === host?.dataset.id);
  if (!order || !session) return;
  const download = event.target.closest<HTMLAnchorElement>("a[data-download]");
  if (download && session.role === "partner") {
    event.preventDefault();
    await partnerActions.download(download, order);
    return;
  }
  if (!button) return;
  if (button.dataset.action === "address" && session.role === "owner") await copyText(order.address);
  if (button.dataset.action === "tracking" && order.tracking_number) await copyText(order.tracking_number);
  if (button.dataset.action === "edit" && session.role === "owner") orderForm.open(order);
  if (button.dataset.action === "delete" && session.role === "owner") {
    deletingId = order.id;
    formError("deleteError", null);
    element("deleteDescription").textContent = `确认删除订单 #${order.id}${order.xianyu_id ? `（${order.xianyu_id}）` : ""}？`;
    deleteDialog.showModal();
  }
  if (session?.role === "partner") await partnerActions.run(button, order);
});

document.addEventListener("visibilitychange", () => {
  if (!document.hidden && session && !document.querySelector("dialog[open]")) void refresh(true);
});

async function boot(): Promise<void> {
  try { await enter(await request<Session>("/auth/me")); }
  catch (error) {
    showGate();
    if (!(error instanceof ApiError && error.status === 401)) formError("loginError", error);
  }
}
void boot();
